import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3 } from 'lucide-react';

const BarChart = ({ data, total, currencySymbol }) => (
  <div className="space-y-4">
    {data.map((cat) => {
      const percentage = (cat.value / total) * 100;
      return (
        <div key={cat.name} className="space-y-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }}></div>
              <span className="font-medium text-gray-700 dark:text-gray-300">{cat.name}</span>
            </div>
            <span className="text-sm text-gray-600 dark:text-gray-400">{cat.value.toFixed(2)} {currencySymbol}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-lg h-6">
            <div className="h-6 rounded-lg transition-all duration-700" style={{ width: `${percentage}%`, backgroundColor: cat.color }} />
          </div>
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{cat.count} dépense(s)</span>
            <span>{percentage.toFixed(1)}%</span>
          </div>
        </div>
      );
    })}
  </div>
);

const PieChart = ({ data, total, currencySymbol }) => {
  let cumulativePercentage = 0;
  return (
    <div className="flex flex-col lg:flex-row items-center gap-6">
      <div className="relative w-80 h-80">
        <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-90">
          {data.map((cat) => {
            const percentage = (cat.value / total) * 100;
            const angle = (percentage / 100) * 360;
            const startAngle = (cumulativePercentage / 100) * 360;
            const startAngleRad = (startAngle * Math.PI) / 180;
            const endAngleRad = ((startAngle + angle) * Math.PI) / 180;
            const largeArcFlag = angle > 180 ? 1 : 0;
            const x1 = 100 + 80 * Math.cos(startAngleRad);
            const y1 = 100 + 80 * Math.sin(startAngleRad);
            const x2 = 100 + 80 * Math.cos(endAngleRad);
            const y2 = 100 + 80 * Math.sin(endAngleRad);
            const pathData = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
            cumulativePercentage += percentage;
            return (
              <path key={cat.name} d={pathData} fill={cat.color} className="hover:opacity-80 transition-opacity cursor-pointer" title={`${cat.name}: ${percentage.toFixed(1)}%`} />
            );
          })}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{total.toFixed(0)}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{currencySymbol}</div>
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-3">
        {data.map((cat) => {
          const percentage = (cat.value / total) * 100;
          return (
            <div key={cat.name} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900">
              <div className="w-5 h-5 rounded-full" style={{ backgroundColor: cat.color }}></div>
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{cat.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{cat.count} dépense(s)</div>
              </div>
              <div className="text-right">
                <div className="font-semibold text-gray-900 dark:text-white">{cat.value.toFixed(2)} {currencySymbol}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HorizontalChart = ({ data, total, currencySymbol }) => (
  <div className="space-y-4">
    {data.map((cat) => {
      const percentage = (cat.value / total) * 100;
      return (
        <div key={cat.name} className="space-y-2">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 flex-1">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }}></div>
              <span className="font-medium text-gray-700 truncate">{cat.name}</span>
            </div>
            <div className="text-right flex-shrink-0 ml-4">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">{cat.value.toFixed(2)} {currencySymbol}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{percentage.toFixed(1)}%</div>
            </div>
          </div>
          <div className="relative w-full">
            <div className="w-full bg-gray-200 rounded-full h-8">
              <div
                className="h-8 rounded-full transition-all duration-1000 flex items-center justify-center"
                style={{ width: `${Math.max(percentage, 5)}%`, backgroundColor: cat.color }}
              >
                {percentage > 10 && <span className="text-white text-xs font-medium">{cat.count}</span>}
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

export const ExpenseCharts = ({ visible, chartType, setChartType, data, total, currencySymbol }) => {
  if (!visible) return null;
  
  const chartRenderers = { bars: BarChart, pie: PieChart, horizontal: HorizontalChart };
  const ChartComponent = chartRenderers[chartType] || BarChart;
  
  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Répartition des dépenses par catégorie
          </CardTitle>
          <Select value={chartType} onValueChange={setChartType}>
            <SelectTrigger className="w-full sm:w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="bars">Barres verticales</SelectItem>
              <SelectItem value="horizontal">Barres horizontales</SelectItem>
              <SelectItem value="pie">Camembert</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {data.length > 0 ? (
          <div className="space-y-4">
            <div className="text-center text-sm text-gray-600 mb-4">
              Total analysé: <span className="font-semibold">{total.toFixed(2)} {currencySymbol}</span> {' '} {data.length} catégorie(s) active(s)
            </div>
            <ChartComponent data={data} total={total} currencySymbol={currencySymbol} />
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Aucune donnée à afficher</p>
            <p className="text-sm">Ajoutez des dépenses pour voir les graphiques</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
